import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { feedbackSubmitApi, questionnaireAnswersApi, questionnairesApi } from "../../services/FeedbackServices";

export default function FeedbackTablet() {
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [answerSet, setAnswerSet] = useState(null);
  const options = useMemo(() => answerSet?.answer_details || [], [answerSet]);

  const answerType = answerSet?.answer_type || "radio";

  const [selected, setSelected] = useState(null);
  const [selectedMulti, setSelectedMulti] = useState([]);
  const [text, setText] = useState("");
  const [additionalText, setAdditionalText] = useState("");

  useEffect(() => {
    questionnairesApi("get", null, { is_active: true })
      .then((res) => {
        const list = res?.data || [];
        const q = list.find((x) => x?.is_active) || list[0] || null;
        setActiveQuestion(q);
      })
      .catch(() => {
        setActiveQuestion(null);
      });
  }, []);

  useEffect(() => {
    if (!activeQuestion?.id) {
      setAnswerSet(null);
      return;
    }
    questionnaireAnswersApi("get", null, { questionnaire_id: activeQuestion.id })
      .then((res) => {
        const list = res?.data || [];
        setAnswerSet(list?.[0] || null);
      })
      .catch(() => {
        setAnswerSet(null);
      });
  }, [activeQuestion?.id]);

  useEffect(() => {
    setSelected(null);
    setSelectedMulti([]);
    setText("");
    setAdditionalText("");
  }, [answerSet?.id]);

  const canSubmit = useMemo(() => {
    if (!activeQuestion?.id) return false;
    if (answerType === "radio") return !!selected;
    if (answerType === "checkbox") return (selectedMulti || []).length > 0;
    if (answerType === "text") return String(text || "").trim().length > 0;
    return false;
  }, [activeQuestion?.id, answerType, selected, selectedMulti, text]);

  const handleSubmit = () => {
    if (!activeQuestion?.id || !answerSet?.id || !canSubmit) {
      return;
    }

    const selectedOptions =
      answerType === "radio"
        ? [selected]
        : answerType === "checkbox"
          ? selectedMulti
          : [];

    const additionalFeedback =
      answerType === "text" ? text : String(additionalText || "").trim() ? additionalText : null;

    const payload = {
      questionnaire_id: activeQuestion.id,
      questionnaire_answer_id: answerSet.id,
      selected_options: selectedOptions,
      additional_feedback: additionalFeedback,
    };

    feedbackSubmitApi(payload)
      .then(() => {
        setSelected(null);
        setSelectedMulti([]);
        setText("");
        setAdditionalText("");
      })
      .catch(() => {});
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "#E9EFFB", minHeight: "100vh" }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 2 }}>
              {activeQuestion?.question || "No active question"}
            </Typography>

            {(answerType === "radio" || answerType === "checkbox") && (
              <Grid container spacing={2}>
                {options.map((o) => {
                  const isSelected =
                    answerType === "radio" ? selected === o.value : (selectedMulti || []).includes(o.value);

                  const toggle = () => {
                    if (answerType === "radio") {
                      setSelected(o.value);
                      return;
                    }

                    setSelectedMulti((prev) => {
                      const list = Array.isArray(prev) ? prev : [];
                      return list.includes(o.value) ? list.filter((x) => x !== o.value) : [...list, o.value];
                    });
                  };

                  return (
                    <Grid item xs={6} key={o.value}>
                      <Button
                        fullWidth
                        variant={isSelected ? "contained" : "outlined"}
                        onClick={toggle}
                        sx={{ height: 72, fontSize: 20, justifyContent: "space-between" }}
                      >
                        <span>{o.label}</span>
                        <span style={{ fontSize: 26 }}>{o.emoji || ""}</span>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {answerType === "text" && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Your Answer"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </Box>
            )}

            {answerType !== "text" && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Additional Feedback (Optional)"
                  value={additionalText}
                  onChange={(e) => setAdditionalText(e.target.value)}
                />
              </Box>
            )}

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button variant={"contained"} onClick={handleSubmit} disabled={!canSubmit} className={"text-capitalize"}>
                Submit
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
